import cv from '@techstark/opencv-js'
import {
	Home,
	Camera,
	Crop,
	RotateCw,
	Sliders,
	Sun,
	ImagesIcon,
	Maximize2Icon,
	Minimize2Icon,
	WandSparklesIcon,
} from 'lucide-react'
import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { Button } from '#app/components/ui/button.js'
import {
	useFullScreenToggle,
	useIsFullScreen,
} from '#app/utils/hooks/use-fullscreen.js'
import { cn } from '#app/utils/misc.js'

interface Point {
	x: number
	y: number
}

export default function Component() {
	const [image, setImage] = useState<string | null>(null)
	const [isCameraReady, setIsCameraReady] = useState(false)
	const [isCropping, setIsCropping] = useState<boolean>(false)
	const [cropPoints, setCropPoints] = useState<Point[]>([])
	const [draggedPointIndex, setDraggedPointIndex] = useState<number | null>(
		null,
	)
	const [draggedEdgeIndex, setDraggedEdgeIndex] = useState<number | null>(null)
	const [dragStart, setDragStart] = useState<Point | null>(null)
	const [isOpenCVReady, setIsOpenCVReady] = useState(false)
	const videoRef = useRef<HTMLVideoElement>(null)
	const imageRef = useRef<HTMLImageElement>(null)
	const canvasRef = useRef<HTMLCanvasElement>(null)
	const previewRef = useRef<HTMLCanvasElement>(null)
	const streamRef = useRef<MediaStream | null>(null)
	const fileInputRef = useRef<HTMLInputElement>(null)
	const rafRef = useRef<number | null>(null)
	const [processingMessage, setProcessingMessage] = useState('')

	const toggleFullScreen = useFullScreenToggle()
	const isFullScreen = useIsFullScreen()

	const setupCamera = async () => {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				video: {
					facingMode: { ideal: 'environment' }, // Prefer back camera
					width: { ideal: 9999 }, // Request the highest possible width
					height: { ideal: 9999 }, // Request the highest possible height
					frameRate: { ideal: 60, max: 120 }, // Try for high FPS (if supported)
				},
			})
			if (videoRef.current) {
				videoRef.current.srcObject = stream
				streamRef.current = stream
				setIsCameraReady(true)
			}
		} catch (err) {
			console.error('Error accessing the camera:', err)
			setIsCameraReady(false)
		}
	}

	const stopCamera = () => {
		if (streamRef.current) {
			// Stop each track from the media stream
			streamRef.current.getTracks().forEach((track) => track.stop())
			// Optionally, clear the video element's source object
			if (videoRef.current) {
				videoRef.current.srcObject = null
			}
			// Reset the stream reference
			streamRef.current = null
			setIsCameraReady(false)
		}
	}

	useEffect(() => {
		void setupCamera()

		if (cv) {
			setIsOpenCVReady(true)
		}

		return () => {
			// Call stopCamera when the component is unmounted
			stopCamera()
		}
	}, [])

	useEffect(() => {
		if (image && imageRef.current) {
			const { offsetWidth: width, offsetHeight: height } = imageRef.current
			setCropPoints([
				{ x: 0.1 * width, y: 0.1 * height },
				{ x: 0.9 * width, y: 0.1 * height },
				{ x: 0.9 * width, y: 0.9 * height },
				{ x: 0.1 * width, y: 0.9 * height },
			])
		}
	}, [image])

	const drawImage = useCallback(() => {
		const canvas = canvasRef.current
		const ctx = canvas?.getContext('2d')
		if (!canvas || !ctx || !imageRef.current) return

		ctx.clearRect(0, 0, canvas.width, canvas.height)
		ctx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height)

		// Draw the quadrilateral
		ctx.strokeStyle = 'rgba(255, 87, 34, 1)'
		ctx.lineWidth = 2
		ctx.beginPath()
		ctx.moveTo(cropPoints[0]?.x ?? 0, cropPoints[0]?.y ?? 0)
		cropPoints.forEach((point, index) => {
			const nextPoint = cropPoints[(index + 1) % cropPoints.length]
			ctx.lineTo(nextPoint?.x ?? 0, nextPoint?.y ?? 0)
		})
		ctx.closePath()
		ctx.stroke()

		// Draw the handle points
		cropPoints.forEach((point) => {
			ctx.strokeStyle = 'rgba(255, 87, 34, 1)'
			ctx.lineWidth = 2
			ctx.beginPath()
			ctx.arc(point.x, point.y, 6, 0, 2 * Math.PI)
			ctx.stroke()
		})
	}, [cropPoints])

	useEffect(() => {
		if (image) {
			drawImage()
		}
	}, [cropPoints, image, drawImage])

	const drawPreview = useCallback(() => {
		const previewCanvas = previewRef.current
		const pctx = previewCanvas?.getContext('2d')
		const canvas = canvasRef.current
		if (
			!previewCanvas ||
			!pctx ||
			!canvas ||
			(draggedPointIndex === null && draggedEdgeIndex === null)
		)
			return

		const zoomFactor = 2
		const previewSize = 100

		pctx.clearRect(0, 0, previewCanvas.width, previewCanvas.height)

		let previewPoint
		if (draggedPointIndex !== null) {
			previewPoint = cropPoints[draggedPointIndex]
		} else if (draggedEdgeIndex !== null) {
			const nextIndex = (draggedEdgeIndex + 1) % 4
			const point1 = cropPoints[draggedEdgeIndex]
			const point2 = cropPoints[nextIndex]
			if (!point1 || !point2) return
			previewPoint = {
				x: (point1.x + point2.x) / 2,
				y: (point1.y + point2.y) / 2,
			}
		}

		if (!previewPoint) {
			return
		}

		const sourceX = Math.max(
			0,
			Math.min(
				previewPoint.x - previewSize / (2 * zoomFactor),
				canvas.width - previewSize / zoomFactor,
			),
		)
		const sourceY = Math.max(
			0,
			Math.min(
				previewPoint.y - previewSize / (2 * zoomFactor),
				canvas.height - previewSize / zoomFactor,
			),
		)

		pctx.drawImage(
			canvas,
			sourceX,
			sourceY,
			previewSize / zoomFactor,
			previewSize / zoomFactor,
			0,
			0,
			previewSize,
			previewSize,
		)

		// Draw crosshair (blue)
		pctx.strokeStyle = 'rgba(255, 87, 34, 0.6)'
		pctx.lineWidth = 2
		pctx.beginPath()
		pctx.moveTo(previewSize / 2, 0)
		pctx.lineTo(previewSize / 2, previewSize)
		pctx.moveTo(0, previewSize / 2)
		pctx.lineTo(previewSize, previewSize / 2)
		pctx.stroke()

		// Draw handle point or edge midpoint (blue)
		const handleX = (previewPoint.x - sourceX) * zoomFactor
		const handleY = (previewPoint.y - sourceY) * zoomFactor

		pctx.strokeStyle = 'rgba(255, 87, 34, 0.8)'
		pctx.lineWidth = 1.5
		pctx.beginPath()
		pctx.arc(handleX, handleY, 3, 0, 2 * Math.PI)
		pctx.stroke()
	}, [cropPoints, draggedPointIndex, draggedEdgeIndex])

	useEffect(() => {
		if (draggedPointIndex !== null || draggedEdgeIndex !== null) {
			drawPreview()
		}
	}, [draggedPointIndex, draggedEdgeIndex, drawPreview])

	const handleMouseDown = useCallback(
		(e: React.MouseEvent<HTMLCanvasElement>) => {
			const canvas = canvasRef.current
			if (!canvas) return

			const rect = canvas.getBoundingClientRect()
			const x = e.clientX - rect.left
			const y = e.clientY - rect.top

			const dragPointIndex = cropPoints.findIndex((point) => {
				const dx = point.x - x
				const dy = point.y - y
				return Math.sqrt(dx * dx + dy * dy) <= 6
			})

			if (dragPointIndex !== -1) {
				setDraggedPointIndex(dragPointIndex)
			}
		},
		[cropPoints],
	)

	const handleMouseMove = useCallback(
		(e: React.MouseEvent<HTMLCanvasElement>) => {
			const canvas = canvasRef.current
			if (draggedPointIndex === null || !canvas) return

			const rect = canvas.getBoundingClientRect()
			const x = Math.max(0, Math.min(e.clientX - rect.left, canvas.width))
			const y = Math.max(0, Math.min(e.clientY - rect.top, canvas.height))

			setCropPoints((prevPoints) => {
				const newPoints = [...prevPoints]
				newPoints[draggedPointIndex] = { x, y }
				return newPoints
			})

			// Call drawPreview after updating the point
			drawPreview()
		},
		[draggedPointIndex, drawPreview],
	)

	const handleMouseUp = useCallback(() => {
		setDraggedPointIndex(null)
	}, [])

	const getPreviewPosition = useCallback(() => {
		const indexOfInterest = draggedPointIndex ?? draggedEdgeIndex
		if (indexOfInterest === null) return 'bottom-2 right-2'
		switch (indexOfInterest) {
			case 0:
				return 'bottom-2 right-2' // Top-left corner being dragged
			case 1:
				return 'bottom-2 left-2' // Top-right corner being dragged
			case 2:
				return 'top-2 left-2' // Bottom-right corner being dragged
			case 3:
				return 'top-2 right-2' // Bottom-left corner being dragged
			default:
				return 'top-2 right-2'
		}
	}, [draggedPointIndex, draggedEdgeIndex])

	// File selection handler
	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (file) {
			const reader = new FileReader()
			reader.onload = (e) => {
				const result = e.target?.result as string
				setImage(result) // Set the selected image to display
				startCropping()
				drawImageOnCanvas(result)
			}
			reader.readAsDataURL(file)
		}
	}

	const drawImageOnCanvas = (src: string) => {
		const canvas = canvasRef.current
		const context = canvas?.getContext('2d')
		if (canvas && context) {
			const image = new Image()
			image.src = src
			image.onload = () => {
				canvas.width = image.width
				canvas.height = image.height
				context.clearRect(0, 0, canvas.width, canvas.height)
				context.drawImage(image, 0, 0, canvas.width, canvas.height)
				stopCamera()
			}
		}
	}

	const handleUploadClick = () => {
		// Trigger file input when the button is clicked
		if (fileInputRef.current) {
			fileInputRef.current.click()
		}
	}

	const captureImage = () => {
		if (videoRef.current && canvasRef.current) {
			const video = videoRef.current
			const canvas = canvasRef.current

			// Get the full resolution of the camera feed
			const videoWidth = video.videoWidth
			const videoHeight = video.videoHeight

			// Get the displayed size of the video element (affected by CSS scaling)
			const displayedWidth = video.clientWidth
			const displayedHeight = video.clientHeight

			// Get canvas context
			const context = canvas.getContext('2d')
			if (context) {
				// Calculate the scaling factors between the video’s native size and displayed size
				const scaleX = videoWidth / displayedWidth
				const scaleY = videoHeight / displayedHeight

				// We assume `object-fit: cover` is applied, meaning the video is cropped
				let sx = 0,
					sy = 0,
					sWidth = videoWidth,
					sHeight = videoHeight

				if (scaleX > scaleY) {
					// Video is wider than container, so it's cropped horizontally
					sWidth = videoHeight * (displayedWidth / displayedHeight) // Maintain aspect ratio
					sx = (videoWidth - sWidth) / 2 // Center horizontally
				} else {
					// Video is taller than container, so it's cropped vertically
					sHeight = videoWidth * (displayedHeight / displayedWidth) // Maintain aspect ratio
					sy = (videoHeight - sHeight) / 2 // Center vertically
				}

				// Set the canvas size to match the displayed crop dimensions
				canvas.width = sWidth
				canvas.height = sHeight

				// Draw only the visible part from the video to the canvas
				context.drawImage(video, sx, sy, sWidth, sHeight, 0, 0, sWidth, sHeight)

				// Convert the canvas content to a high-quality image
				const imageData = canvas.toDataURL('image/png') // 'image/webp' for better compression
				setImage(imageData)

				// Continue cropping or processing
				startCropping()
				stopCamera()
			}
		}
	}

	const handleRetake = async () => {
		setImage(null)
		startCropping()
		await setupCamera()
	}

	const applyPerspectiveCorrection = () => {
		if (!imageRef.current || cropPoints.length !== 4 || !isOpenCVReady) {
			console.error('Prerequisites not met for perspective correction')
			return null
		}

		try {
			// Create a temporary canvas
			const tempCanvas = document.createElement('canvas')
			const tempCtx = tempCanvas.getContext('2d')
			if (!tempCtx) {
				console.error('Failed to get 2D context for temporary canvas')
				return null
			}

			// Set the temporary canvas size to match the original image
			tempCanvas.width = imageRef.current.naturalWidth
			tempCanvas.height = imageRef.current.naturalHeight

			// Draw the original image onto the temporary canvas
			tempCtx.drawImage(
				imageRef.current,
				0,
				0,
				tempCanvas.width,
				tempCanvas.height,
			)

			// Read the image from the temporary canvas
			console.log('Reading image from temporary canvas')
			const src = cv.imread(tempCanvas)

			// Calculate the scale factor between the displayed image and the original image
			const scaleX = imageRef.current.naturalWidth / imageRef.current.width
			const scaleY = imageRef.current.naturalHeight / imageRef.current.height

			// Define source points (scaled to match the original image dimensions)
			console.log('Defining source points')
			const srcPoints = cv.matFromArray(4, 1, cv.CV_32FC2, [
				cropPoints[0]!.x * scaleX,
				cropPoints[0]!.y * scaleY,
				cropPoints[1]!.x * scaleX,
				cropPoints[1]!.y * scaleY,
				cropPoints[2]!.x * scaleX,
				cropPoints[2]!.y * scaleY,
				cropPoints[3]!.x * scaleX,
				cropPoints[3]!.y * scaleY,
			])

			const aspectRatioA4 = 595 / 842

			// Define destination points (A4 aspect ratio)
			const dstWidth = Math.round(
				imageRef.current.naturalHeight * aspectRatioA4,
			)
			const dstHeight = Math.round(imageRef.current.naturalHeight)
			console.log('Defining destination points')
			const dstPoints = cv.matFromArray(4, 1, cv.CV_32FC2, [
				0,
				0,
				dstWidth,
				0,
				dstWidth,
				dstHeight,
				0,
				dstHeight,
			])

			// Get perspective transform
			console.log('Getting perspective transform')
			const M = cv.getPerspectiveTransform(srcPoints, dstPoints)

			// Apply perspective transform
			console.log('Applying perspective transform')
			const dst = new cv.Mat()
			cv.warpPerspective(src, dst, M, new cv.Size(dstWidth, dstHeight))

			// Create a new canvas for the output
			const outputCanvas = document.createElement('canvas')
			outputCanvas.width = dstWidth
			outputCanvas.height = dstHeight

			// Display the result on the output canvas
			console.log('Displaying result on output canvas')
			cv.imshow(outputCanvas, dst)

			// Get the corrected image data
			const correctedImageData = outputCanvas.toDataURL('image/png')

			// Clean up
			console.log('Cleaning up OpenCV objects')
			src.delete()
			dst.delete()
			M.delete()
			srcPoints.delete()
			dstPoints.delete()

			console.log('Perspective correction completed successfully')
			return correctedImageData
		} catch (error) {
			console.error('Error in applyPerspectiveCorrection:', error)
			return null
		}
	}

	const handleSave = () => {
		if (isOpenCVReady) {
			setProcessingMessage('Processing image...')
			const correctedImageData = applyPerspectiveCorrection()
			if (correctedImageData) {
				console.log('Perspective correction applied successfully')
				setProcessingMessage('Perspective correction applied successfully')

				// Create a download link for the user
				const downloadLink = document.createElement('a')
				downloadLink.href = correctedImageData
				downloadLink.download = 'scanned_document.png'
				document.body.appendChild(downloadLink)
				downloadLink.click()
				document.body.removeChild(downloadLink)

				setProcessingMessage('')
			} else {
				console.error('Failed to apply perspective correction')
				setProcessingMessage(
					'Failed to apply perspective correction. Please try again.',
				)
			}
		} else {
			console.log('OpenCV.js is not ready yet. Please wait and try again.')
			setProcessingMessage(
				'OpenCV.js is not ready yet. Please wait and try again.',
			)
		}
	}

	const startCropping = () => {
		setIsCropping(!isCropping)
	}

	const getClientCoordinates = (
		e: React.MouseEvent | React.TouchEvent | MouseEvent | TouchEvent,
	): Point => {
		if ('touches' in e) {
			return { x: e.touches[0]!.clientX, y: e.touches[0]!.clientY }
		}
		return { x: e.clientX, y: e.clientY }
	}

	const handleDragStart = useCallback((index: number, isEdge: boolean) => {
		if (isEdge) {
			setDraggedEdgeIndex(index)
		} else {
			setDraggedPointIndex(index)
		}
	}, [])

	const updateCropPoints = useCallback(
		(clientX: number, clientY: number) => {
			if (imageRef.current && dragStart) {
				const rect = imageRef.current.getBoundingClientRect()
				const deltaX = clientX - dragStart.x
				const deltaY = clientY - dragStart.y

				setCropPoints((prevPoints) => {
					const newPoints = [...prevPoints]

					if (draggedPointIndex !== null) {
						newPoints[draggedPointIndex] = {
							x: Math.max(
								0,
								Math.min(newPoints[draggedPointIndex]!.x + deltaX, rect.width),
							),
							y: Math.max(
								0,
								Math.min(newPoints[draggedPointIndex]!.y + deltaY, rect.height),
							),
						}
					} else if (draggedEdgeIndex !== null) {
						const nextIndex = (draggedEdgeIndex + 1) % 4
						if (draggedEdgeIndex % 2 === 1) {
							// Horizontal edge
							const newX1 = Math.max(
								0,
								Math.min(newPoints[draggedEdgeIndex]!.x + deltaX, rect.width),
							)
							const newX2 = Math.max(
								0,
								Math.min(newPoints[nextIndex]!.x + deltaX, rect.width),
							)
							newPoints[draggedEdgeIndex]!.x = newX1
							newPoints[nextIndex]!.x = newX2
						} else {
							// Vertical edge
							const newY1 = Math.max(
								0,
								Math.min(newPoints[draggedEdgeIndex]!.y + deltaY, rect.height),
							)
							const newY2 = Math.max(
								0,
								Math.min(newPoints[nextIndex]!.y + deltaY, rect.height),
							)
							newPoints[draggedEdgeIndex]!.y = newY1
							newPoints[nextIndex]!.y = newY2
						}
					}

					return newPoints
				})

				setDragStart({ x: clientX, y: clientY })
			}
		},
		[dragStart, draggedPointIndex, draggedEdgeIndex],
	)

	const handleDragMove = useCallback(
		(e: MouseEvent | TouchEvent) => {
			e.preventDefault()
			const { x, y } = getClientCoordinates(e)

			if (rafRef.current !== null) {
				cancelAnimationFrame(rafRef.current)
			}

			rafRef.current = requestAnimationFrame(() => {
				updateCropPoints(x, y)
			})
		},
		[updateCropPoints],
	)

	const handleDragEnd = useCallback(() => {
		setDraggedPointIndex(null)
		setDraggedEdgeIndex(null)
		setDragStart(null)
		if (rafRef.current !== null) {
			cancelAnimationFrame(rafRef.current)
			rafRef.current = null
		}
	}, [])

	useEffect(() => {
		const handleTouchStart = (e: TouchEvent) => {
			if (draggedPointIndex !== null || draggedEdgeIndex !== null) {
				e.preventDefault()
				const { x, y } = getClientCoordinates(e)
				setDragStart({ x, y })
			}
		}

		if (isCropping) {
			const handleMouseDown = (e: MouseEvent) => handleTouchStart(e as any)
			window.addEventListener('mousedown', handleMouseDown, { passive: false })
			window.addEventListener('touchstart', handleTouchStart, {
				passive: false,
			})
			window.addEventListener('mousemove', handleDragMove)
			window.addEventListener('touchmove', handleDragMove, { passive: false })
			window.addEventListener('mouseup', handleDragEnd)
			window.addEventListener('touchend', handleDragEnd)

			return () => {
				window.removeEventListener('mousedown', handleMouseDown)
				window.removeEventListener('touchstart', handleTouchStart)
				window.removeEventListener('mousemove', handleDragMove)
				window.removeEventListener('touchmove', handleDragMove)
				window.removeEventListener('mouseup', handleDragEnd)
				window.removeEventListener('touchend', handleDragEnd)
			}
		}
	}, [
		isCropping,
		handleDragMove,
		handleDragEnd,
		draggedPointIndex,
		draggedEdgeIndex,
	])

	const memoizedCropOverlay = useMemo(() => {
		if (!isCropping || cropPoints.length !== 4) return null

		const edgeMiddlePoints = cropPoints.map((point, i) => {
			const nextPoint = cropPoints[(i + 1) % 4]!
			const middlePoint = {
				x: (point.x + nextPoint.x) / 2,
				y: (point.y + nextPoint.y) / 2,
			}
			const angle = Math.atan2(nextPoint.y - point.y, nextPoint.x - point.x)
			return { ...middlePoint, angle }
		})

		return (
			<>
				<svg className="pointer-events-none absolute left-0 top-0 h-full w-full">
					<path
						d={`M${cropPoints.map((p) => `${p.x},${p.y}`).join(' L')} Z`}
						fill="none"
						stroke="#ff5722"
						strokeWidth="2"
						vectorEffect="non-scaling-stroke"
					/>
				</svg>
				{cropPoints.map((point, index) => (
					<div
						key={`point-${index}`}
						className="absolute cursor-move rounded-full border-2 border-orange-500 bg-white"
						style={{
							width: '28px',
							height: '28px',
							left: `${(point.x / imageRef.current!.offsetWidth) * 100}%`,
							top: `${(point.y / imageRef.current!.offsetHeight) * 100}%`,
							transform: 'translate(-50%, -50%)',
							backgroundColor: 'rgba(255, 255, 255, 0.5)',
							boxShadow: '0 0 0 1px rgba(59, 130, 246, 0.5)',
							touchAction: 'none',
						}}
						onMouseDown={() => handleDragStart(index, false)}
						onTouchStart={() => handleDragStart(index, false)}
					/>
				))}
				{edgeMiddlePoints.map((point, index) => (
					<div
						key={`edge-${index}`}
						className={`absolute border-2 border-orange-500 ${index % 2 === 0 ? 'cursor-ns-resize' : 'cursor-ew-resize'}`}
						style={{
							width: '42px',
							height: '28px',
							left: `${(point.x / imageRef.current!.offsetWidth) * 100}%`,
							top: `${(point.y / imageRef.current!.offsetHeight) * 100}%`,
							transform: `translate(-50%, -50%) rotate(${point.angle}rad)`,
							backgroundColor: 'rgba(255, 255, 255, 0.5)',
							boxShadow: '0 0 0 1px rgba(59, 130, 246, 0.5)',
							touchAction: 'none',
						}}
						onMouseDown={() => handleDragStart(index, true)}
						onTouchStart={() => handleDragStart(index, true)}
					/>
				))}
			</>
		)
	}, [isCropping, cropPoints, handleDragStart])

	return (
		<div className="flex min-h-dvh items-center justify-center bg-gray-100 p-0 sm:p-4">
			<div className="h-full w-full overflow-hidden bg-gray-900 text-white shadow-xl sm:h-auto sm:w-full sm:max-w-md sm:rounded-lg">
				<div className="flex h-dvh flex-col sm:aspect-[9/16] sm:h-auto">
					{/* Top bar */}
					<div className="flex items-center justify-between p-4">
						<Button variant="ghost" size="icon">
							<Home className="h-6 w-6" />
						</Button>
						<div className="text-lg font-semibold">
							{image ? 'Scan 22 Sep 2024' : 'Document Capture'}
						</div>
						<div>
							<Button variant="ghost" size="icon">
								{isFullScreen ? (
									<Minimize2Icon
										className="h-6 w-6"
										onClick={() => toggleFullScreen()}
									/>
								) : (
									<Maximize2Icon
										className="h-6 w-6"
										onClick={() => toggleFullScreen()}
									/>
								)}
							</Button>
						</div>
					</div>

					{/* Camera viewfinder or Image preview */}
					<div className="relative flex-1 bg-gray-800">
						<div
							className={cn('m-4 flex h-full items-center justify-center')}
							style={{ display: !image ? 'none' : undefined }}
						>
							<div className="relative mb-8 h-full w-full">
								<canvas
									ref={canvasRef}
									className="absolute inset-0 hidden h-full w-full"
									onMouseDown={handleMouseDown}
									onMouseMove={handleMouseMove}
									onMouseUp={handleMouseUp}
									onMouseLeave={handleMouseUp}
								/>
								<img
									ref={imageRef}
									src={image ?? ''}
									className="absolute inset-0 h-full w-full"
									onLoad={() => {
										if (canvasRef.current && imageRef.current) {
											canvasRef.current.width = imageRef.current.width
											canvasRef.current.height = imageRef.current.height
											drawImage()
										}
									}}
								/>
								{memoizedCropOverlay}
								{(draggedPointIndex !== null || draggedEdgeIndex !== null) && (
									<canvas
										ref={previewRef}
										width={100}
										height={100}
										className={`absolute ${getPreviewPosition()} rounded-full border-2 border-orange-500`}
									/>
								)}
							</div>
						</div>
						<video
							ref={videoRef}
							autoPlay
							playsInline
							muted
							className="absolute inset-0 h-full w-full object-cover"
							style={{ display: image ? 'none' : undefined }}
						/>
						{!isCameraReady && !image && (
							<div className="absolute inset-0 flex items-center justify-center text-gray-500">
								Camera not available
							</div>
						)}
					</div>

					{image ? (
						<>
							{/* Auto-detect and No crop buttons */}
							<div className="flex justify-center space-x-4 bg-gray-800 p-2">
								<Button
									variant="secondary"
									className="bg-gray-700 px-6 py-2 text-white hover:bg-gray-600"
								>
									<WandSparklesIcon className="mr-2 h-4 w-4" />
									Auto-detect
								</Button>
								<Button
									variant="secondary"
									className="bg-gray-700 px-6 py-2 text-white hover:bg-gray-600"
								>
									<Crop className="mr-2 h-4 w-4" />
									No crop
								</Button>
							</div>

							{/* Bottom toolbar */}
							<div className="flex items-center justify-between bg-gray-900 px-4 py-4">
								<Button
									variant="ghost"
									size="icon"
									onClick={handleRetake}
									className="flex flex-col items-center"
								>
									<Camera className="h-6 w-6" />
									<span className="mt-1 text-xs">Retake</span>
								</Button>
								<Button
									variant="ghost"
									size="icon"
									className={cn(
										'flex flex-col items-center',
										isCropping && 'text-orange-500',
									)}
									onClick={startCropping}
								>
									<Crop className="h-6 w-6" />
									<span className="mt-1 text-xs">Crop</span>
								</Button>
								<Button
									variant="ghost"
									size="icon"
									className="flex flex-col items-center"
								>
									<RotateCw className="h-6 w-6" />
									<span className="mt-1 text-xs">Rotate</span>
								</Button>
								<Button
									variant="ghost"
									size="icon"
									className="flex flex-col items-center"
								>
									<Sliders className="h-6 w-6" />
									<span className="mt-1 text-xs">Filters</span>
								</Button>
								<Button
									variant="ghost"
									size="icon"
									className="flex flex-col items-center"
								>
									<Sun className="h-6 w-6" />
									<span className="mt-1 text-xs">Adjust</span>
								</Button>
							</div>

							{/* Save button */}
							<div className="bg-gray-900 p-4">
								<Button
									className="w-full bg-orange-500 text-white hover:bg-orange-600"
									onClick={handleSave}
									disabled={!isOpenCVReady}
								>
									{isOpenCVReady
										? 'Save with Perspective Correction'
										: 'Loading OpenCV...'}
								</Button>
							</div>
							{processingMessage && (
								<div className="mt-2 text-center text-sm text-gray-300">
									{processingMessage}
								</div>
							)}
						</>
					) : (
						<div className="flex h-36 items-center justify-between p-4">
							<Button variant="ghost" size="icon" onClick={handleUploadClick}>
								<ImagesIcon className="h-6 w-6" />
							</Button>

							<input
								type="file"
								ref={fileInputRef}
								accept="image/*"
								onChange={handleFileChange}
								style={{ display: 'none' }} // Hidden file input
							/>
							<div className="flex items-center space-x-4">
								<Button
									variant="outline"
									size="icon"
									className="h-16 w-16 rounded-full border-4"
									onClick={captureImage}
									disabled={!isCameraReady}
								>
									<Camera className="h-8 w-8" />
								</Button>
							</div>
							<Button variant="ghost" size="icon">
								<Sun className="hidden h-6 w-6" />
							</Button>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}
